import type {
    NotificationBannerCarouselItem,
    NotificationBannerProps,
} from '@enter/business-components/components/notification-banner';
import type {
    ActiveBannerMessage,
    GetActiveBannerMessagesResponse,
} from '@enter/api';

const ENTER_HOME_BANNER_COUNTDOWN_STORAGE_PREFIX =
    'enter-home:notification-banner-countdown:';

interface NotificationBannerCountdownStorage {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
}

interface ResolveNotificationBannerCountdownOptions {
    nowMs?: number;
    storage?: NotificationBannerCountdownStorage | null;
}

function getNotificationBannerCountdownStorage(): NotificationBannerCountdownStorage | null {
    if (typeof window === 'undefined') return null;

    try {
        return window.localStorage;
    } catch {
        return null;
    }
}

function isValidCountdownEndTime(value: string | null | undefined): value is string {
    return Boolean(value && Number.isFinite(Date.parse(value)));
}

/**
 * 将 landing 公共接口的 Countdown 配置补齐为共享组件消费的绝对截止时间。
 * 后端已解析的 `countdown_end_time` 始终优先。
 */
export function resolveEnterHomeNotificationBannerCountdownEndTime(
    message: ActiveBannerMessage,
    options: ResolveNotificationBannerCountdownOptions = {}
): string | null {
    if (isValidCountdownEndTime(message.countdown_end_time)) {
        return message.countdown_end_time;
    }

    const config = message.content_config.countdownConfig;
    if (!config) return null;

    if (config.mode === 'activity') {
        return isValidCountdownEndTime(config.endTime) ? config.endTime : null;
    }

    if (!Number.isFinite(config.durationSeconds) || config.durationSeconds <= 0) {
        return null;
    }

    const nowMs = options.nowMs ?? Date.now();
    const storage = options.storage === undefined
        ? getNotificationBannerCountdownStorage()
        : options.storage;
    const storageKey = `${ENTER_HOME_BANNER_COUNTDOWN_STORAGE_PREFIX}${message.id}:${config.durationSeconds}`;

    try {
        const persistedEndTime = storage?.getItem(storageKey);
        if (isValidCountdownEndTime(persistedEndTime)) {
            return persistedEndTime;
        }
    } catch {
        // localStorage 不可用时仍可在当前 mount 内使用本次计算值。
    }

    const resolvedEndTime = new Date(nowMs + config.durationSeconds * 1000).toISOString();
    try {
        storage?.setItem(storageKey, resolvedEndTime);
    } catch {
        // localStorage 写入失败不阻断 Banner 展示。
    }
    return resolvedEndTime;
}

/** 为 enter-home landing 响应补齐共享 NotificationBanner 所需的倒计时字段。 */
export function resolveEnterHomeNotificationBannerResponse(
    response: GetActiveBannerMessagesResponse,
    options: ResolveNotificationBannerCountdownOptions = {}
): GetActiveBannerMessagesResponse {
    return {
        ...response,
        data: {
            ...response.data,
            messages: response.data.messages.map((message) => ({
                ...message,
                countdown_end_time: resolveEnterHomeNotificationBannerCountdownEndTime(
                    message,
                    options
                ),
            })),
        },
    };
}

/**
 * 关闭轮播时优先使用当前激活项，普通态回退到 Hook 返回的首条 Banner。
 */
export function resolveEnterHomeNotificationBannerCloseId(
    banner: NotificationBannerProps | null,
    activeBanner?: NotificationBannerCarouselItem
): number | null {
    const id = activeBanner?.id ?? banner?.id;
    if (id === undefined) return null;

    const numericId = Number(id);
    return Number.isFinite(numericId) ? numericId : null;
}
